/* ============================================================================
   06 · Cart drawer & add-to-bag
   ----------------------------------------------------------------------------
   Pattern: the most-touched motion on any commerce site — add-to-bag → drawer
   open → success confirmation → dismiss. Everything about it either builds or
   erodes purchase confidence.

   What's being decided here:
     • The arc between button and cart. A "fly-to-cart" clone launches from
       the primary image and lands on the cart icon. Decides whether it reads
       as delightful (it's cheap; it's answering a real question) or as toy
       (it draws attention away from the drawer opening). Currently: ~480ms,
       eases with emphasis, opacity dips at the end so the landing doesn't
       stack on the cart button.
     • Add-to-bag button feedback. The label swaps with a spring pop; the
       button also darkens for --mo-quick. Spring is licensed here because the
       moment is affirmative and user-initiated. Anywhere else this looks
       cheap; here it says "yes, that worked."
     • Drawer entrance. --mo-slow with --mo-ease-entrance. The scrim fades on
       --mo-base, ~120ms after the panel starts — the panel arrives first and
       the backdrop settles behind it, rather than the backdrop announcing the
       panel is coming.
     • Success flash. A ring + checkmark overlays the drawer for 900ms after
       an add. It's the "your action registered" beat. The GSAP stroke draw-on
       is stand-in for a Lottie; src/lottie.js is wired and mountLottie()
       drops in the moment a check.json is authored.
     • Quantity nudge. Number scale-flips like an odometer digit so the change
       is visible from the corner of the eye. If you don't animate it, people
       tap +/- again thinking nothing happened.

   Reduced motion: drawer opens without a slide (opacity crossfade + panel
   stays right-aligned); fly-to-cart is skipped entirely; success flash still
   fires with a static check icon; quantity number just changes text.
   ========================================================================= */

import { initMotion, isReduced, MOTION } from '../../src/motion.js';
import { mountLabBar } from '../../src/lab.js';

const { gsap } = initMotion();
mountLabBar({ title: '06 · Cart drawer' });

/* --- State (in-memory shopping cart) -------------------------------------- */
const cart = [];
let nextId = 1;

const els = {
  drawer: document.querySelector('[data-drawer]'),
  panel: document.querySelector('[data-drawer-panel]'),
  scrim: document.querySelector('[data-drawer-scrim]'),
  close: document.querySelectorAll('[data-drawer-close]'),
  open: document.querySelector('[data-cart-open]'),
  cartCount: document.querySelector('[data-cart-count]'),
  lines: document.querySelector('[data-drawer-lines]'),
  empty: document.querySelector('[data-drawer-empty]'),
  foot: document.querySelector('[data-drawer-foot]'),
  subtotal: document.querySelector('[data-drawer-subtotal]'),
  atb: document.querySelector('[data-atb]'),
  atbLabel: document.querySelector('[data-atb-label]'),
  colorName: document.querySelector('[data-color-name]'),
  swatches: document.querySelectorAll('[data-color]'),
  sizes: document.querySelectorAll('[data-size]'),
  shot: document.querySelector('[data-pdp-shot]'),
  fly: document.querySelector('[data-fly]'),
  flash: document.querySelector('[data-flash]'),
  flashRing: document.querySelector('.drawer__flash-ring'),
  flashTick: document.querySelector('.drawer__flash-tick'),
  flashCopy: document.querySelector('.drawer__flash-copy'),
};

let selected = { color: 'Caribbean Blue', size: 'S' };

/* --- Selection UI --------------------------------------------------------- */
els.swatches.forEach((btn) =>
  btn.addEventListener('click', () => {
    els.swatches.forEach((b) => b.setAttribute('aria-pressed', 'false'));
    btn.setAttribute('aria-pressed', 'true');
    selected.color = btn.dataset.color;

    // Label crossfade so the color name doesn't hard-swap under the eye.
    gsap
      .timeline()
      .to(els.colorName, { opacity: 0, y: -4, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
      .set(els.colorName, { textContent: selected.color })
      .to(els.colorName, { opacity: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance });

    // Recolor the PDP hero (a stand-in — in a real page this would swap the
    // image). Uses --mo-base so it feels connected to the swatch, not to the
    // color-label crossfade above.
    const styles = getComputedStyle(document.documentElement);
    const swatchColor = getComputedStyle(btn).backgroundColor;
    els.shot.style.transition = `background ${MOTION.dur.base}s ${styles.getPropertyValue('--mo-ease-standard')}`;
    els.shot.style.background = `linear-gradient(155deg, ${swatchColor}, var(--c-obsidian) 120%)`;
  })
);

els.sizes.forEach((btn) =>
  btn.addEventListener('click', () => {
    els.sizes.forEach((b) => b.setAttribute('aria-pressed', 'false'));
    btn.setAttribute('aria-pressed', 'true');
    selected.size = btn.dataset.size;
  })
);

/* --- Add-to-bag ----------------------------------------------------------- */
els.atb.addEventListener('click', () => addToCart());

function addToCart() {
  const item = {
    id: nextId++,
    name: 'Catarina One-Pocket Top',
    variant: `${selected.color} · ${selected.size}`,
    price: 58,
    qty: 1,
    color: selected.color,
  };
  cart.push(item);

  // Kick the button feedback and the fly-to-cart in the same frame so the
  // page reads as one gesture.
  playAtbFeedback();
  if (!isReduced()) playFlyToCart();
  renderCart({ addedId: item.id });
  bumpCount({ delta: 1 });

  // Open the drawer just after the fly lands, so the user sees the arc
  // resolve into the drawer opening (and doesn't miss the fly because the
  // drawer already covered the destination).
  const wait = isReduced() ? 0 : 320;
  setTimeout(() => openDrawer({ withFlash: true }), wait);
}

function playAtbFeedback() {
  const button = els.atb;
  const label = els.atbLabel;
  const added = cart.length > 1; // second+ add shows "Added" for confirmation
  const nextText = 'Added to bag';

  gsap.killTweensOf([button, label]);

  gsap
    .timeline()
    .to(label, {
      yPercent: -120,
      opacity: 0,
      duration: MOTION.dur.quick,
      ease: MOTION.ease.exit,
    })
    .set(label, { textContent: nextText, yPercent: 120 })
    .to(label, {
      yPercent: 0,
      opacity: 1,
      duration: MOTION.dur.slow,
      ease: MOTION.ease.spring,
    })
    .to(
      button,
      {
        scale: 1.015,
        duration: MOTION.dur.quick,
        yoyo: true,
        repeat: 1,
        ease: MOTION.ease.standard,
      },
      '<'
    )
    // After a beat, return the button label to the CTA so a second add works
    // — but only if the drawer is closed. If the drawer is open, "Added" stays
    // as the persistent state.
    .set(label, { textContent: 'Add to bag' }, added ? 1.6 : 2.2);
}

function playFlyToCart() {
  const shot = els.shot.getBoundingClientRect();
  const dest = els.open.getBoundingClientRect();
  const fly = els.fly;

  fly.hidden = false;
  fly.style.background = getComputedStyle(els.shot).background;
  fly.style.opacity = '1';
  fly.style.top = `${shot.top + shot.height * 0.25}px`;
  fly.style.left = `${shot.left + shot.width * 0.25}px`;

  const dx = dest.left + dest.width / 2 - (shot.left + shot.width * 0.25 + 28);
  const dy = dest.top + dest.height / 2 - (shot.top + shot.height * 0.25 + 36);

  gsap.killTweensOf(fly);
  gsap
    .timeline({
      onComplete: () => {
        fly.hidden = true;
        fly.style.opacity = '0';
      },
    })
    // Arc: rise then fall, ending small at the cart icon.
    .to(fly, {
      x: dx,
      y: dy,
      scale: 0.2,
      duration: MOTION.dur.deliberate,
      ease: MOTION.ease.emphasis,
    })
    .to(
      fly,
      { opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit },
      '-=0.18'
    );
}

function bumpCount({ delta }) {
  const count = cart.reduce((sum, l) => sum + l.qty, 0);
  els.cartCount.textContent = String(count);
  els.cartCount.dataset.empty = count === 0 ? 'true' : 'false';

  if (isReduced()) return;
  gsap.killTweensOf(els.cartCount);
  gsap.fromTo(
    els.cartCount,
    { scale: 1 },
    { scale: 1.35, duration: MOTION.dur.quick, ease: MOTION.ease.spring, yoyo: true, repeat: 1 }
  );

  if (delta > 0) {
    // The cart button as a whole also nudges — the badge alone can be missed
    // when it's the same color as the icon.
    gsap.fromTo(
      els.open,
      { rotate: 0 },
      { rotate: -6, duration: MOTION.dur.quick, ease: MOTION.ease.spring, yoyo: true, repeat: 1 }
    );
  }
}

/* --- Drawer open / close -------------------------------------------------- */
let lastFocused = null;

function openDrawer({ withFlash = false } = {}) {
  if (els.drawer.dataset.open === 'true') {
    if (withFlash) playFlash();
    return;
  }
  els.drawer.hidden = false;
  requestAnimationFrame(() => {
    els.drawer.dataset.open = 'true';
    els.drawer.setAttribute('aria-hidden', 'false');
  });
  document.body.style.overflow = 'hidden';
  lastFocused = document.activeElement;
  setTimeout(() => els.close[0]?.focus(), 320);

  if (withFlash) {
    // Wait for the panel to arrive before the flash. Firing before the panel
    // is on-screen means the confirmation happens where the user isn't looking.
    setTimeout(() => playFlash(), isReduced() ? 0 : MOTION.dur.slow * 700);
  }
}

function closeDrawer() {
  if (els.drawer.dataset.open !== 'true') return;
  els.drawer.dataset.open = 'false';
  els.drawer.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  lastFocused?.focus?.();

  const wait = isReduced() ? 0 : MOTION.dur.slow * 1000;
  setTimeout(() => {
    if (els.drawer.dataset.open !== 'true') els.drawer.hidden = true;
  }, wait);
}

els.open.addEventListener('click', () => openDrawer());
els.close.forEach((btn) => btn.addEventListener('click', closeDrawer));
els.scrim.addEventListener('click', closeDrawer);
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeDrawer();
});

/* --- Success flash --------------------------------------------------------
   Two-part animation: the ring pops in (spring), then the stroke draws (base).
   Held for ~800ms, then it fades. If Lottie is loaded and a check.json is
   supplied, this section can be replaced by mountLottie('[data-flash]', ...).
   The bridge is already imported and ready in src/lottie.js. */
function playFlash() {
  els.flash.hidden = false;
  els.flash.setAttribute('aria-hidden', 'false');

  if (isReduced()) {
    gsap.set(els.flashRing, { scale: 1, opacity: 1 });
    gsap.set(els.flashTick, { strokeDashoffset: 0, opacity: 1 });
    gsap.set(els.flashCopy, { opacity: 1, y: 0 });
    setTimeout(() => hideFlash(), 700);
    return;
  }

  gsap.set(els.flashRing, { scale: 0.6, opacity: 0, transformOrigin: '50% 50%' });
  gsap.set(els.flashTick, { strokeDashoffset: 60, opacity: 1 });
  gsap.set(els.flashCopy, { opacity: 0, y: 8 });

  gsap
    .timeline({ onComplete: () => setTimeout(hideFlash, 700) })
    .to(els.flashRing, {
      scale: 1,
      opacity: 1,
      duration: MOTION.dur.slow,
      ease: MOTION.ease.spring,
    })
    .to(
      els.flashTick,
      { strokeDashoffset: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance },
      '-=0.35'
    )
    .to(
      els.flashCopy,
      { opacity: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance },
      '-=0.2'
    );
}

function hideFlash() {
  gsap.to([els.flashRing, els.flashTick, els.flashCopy, els.flash], {
    opacity: 0,
    duration: MOTION.dur.base,
    ease: MOTION.ease.exit,
    onComplete: () => {
      els.flash.hidden = true;
      els.flash.setAttribute('aria-hidden', 'true');
      els.flash.style.opacity = '';
    },
  });
}

/* --- Line rendering + quantity nudges ------------------------------------- */
function renderCart({ addedId } = {}) {
  els.lines.innerHTML = cart
    .map(
      (line) => `
    <li class="line" data-line-id="${line.id}">
      <div class="line__media" style="background:${cardMediaFor(line.color)}"></div>
      <div class="line__body">
        <p class="line__name">${line.name}</p>
        <p class="line__meta">${line.variant}</p>
        <div class="line__qty">
          <button type="button" data-qty-dec aria-label="Decrease">&minus;</button>
          <span class="line__qty-count" data-qty>${line.qty}</span>
          <button type="button" data-qty-inc aria-label="Increase">+</button>
        </div>
        <button class="line__remove" type="button" data-remove>Remove</button>
      </div>
      <div class="line__price">$${line.qty * line.price}</div>
    </li>`
    )
    .join('');

  els.subtotal.textContent = `$${cart.reduce((sum, l) => sum + l.qty * l.price, 0)}`;
  els.empty.hidden = cart.length !== 0;
  els.foot.hidden = cart.length === 0;

  if (addedId != null && !isReduced()) {
    // The new line enters with a fade-rise, same primitive as the pattern
    // shelf — it's what the shopper's eye is already trained for.
    const added = els.lines.querySelector(`[data-line-id="${addedId}"]`);
    if (added) {
      gsap.fromTo(
        added,
        { opacity: 0, y: 16, height: 0 },
        {
          opacity: 1,
          y: 0,
          height: 'auto',
          duration: MOTION.dur.slow,
          ease: MOTION.ease.entrance,
        }
      );
    }
  }
}

function cardMediaFor(color) {
  const key = color.toLowerCase().replace(/\s+/g, '-');
  const map = {
    'caribbean-blue': ['--p-caribbean-blue', '--p-navy'],
    navy: ['--p-navy', '--p-royal-blue'],
    'royal-blue': ['--p-royal-blue', '--p-navy'],
    mauve: ['--p-mauve', '--p-burgundy'],
    'hunter-green': ['--p-hunter-green', '--p-espresso'],
  };
  const stops = map[key] ?? ['--p-navy', '--p-royal-blue'];
  return `linear-gradient(155deg, var(${stops[0]}), var(${stops[1]}) 85%)`;
}

/* --- Line-level interactions (event delegation) --------------------------- */
els.lines.addEventListener('click', (e) => {
  const line = e.target.closest('[data-line-id]');
  if (!line) return;
  const id = Number(line.dataset.lineId);
  const model = cart.find((l) => l.id === id);
  if (!model) return;

  if (e.target.closest('[data-qty-inc]')) {
    model.qty += 1;
    updateQtyReadout(line, model.qty, +1);
    updateLinePrice(line, model);
    els.subtotal.textContent = `$${cart.reduce((sum, l) => sum + l.qty * l.price, 0)}`;
    bumpCount({ delta: 1 });
  } else if (e.target.closest('[data-qty-dec]')) {
    if (model.qty <= 1) {
      removeLine(line, model);
      return;
    }
    model.qty -= 1;
    updateQtyReadout(line, model.qty, -1);
    updateLinePrice(line, model);
    els.subtotal.textContent = `$${cart.reduce((sum, l) => sum + l.qty * l.price, 0)}`;
    bumpCount({ delta: -1 });
  } else if (e.target.closest('[data-remove]')) {
    removeLine(line, model);
  }
});

function updateQtyReadout(line, value, direction) {
  const readout = line.querySelector('[data-qty]');
  if (isReduced()) {
    readout.textContent = String(value);
    return;
  }
  // Odometer-style flip so the change is impossible to miss.
  gsap.killTweensOf(readout);
  gsap
    .timeline()
    .to(readout, {
      yPercent: direction > 0 ? -80 : 80,
      opacity: 0,
      duration: MOTION.dur.quick,
      ease: MOTION.ease.exit,
    })
    .set(readout, { textContent: String(value), yPercent: direction > 0 ? 80 : -80 })
    .to(readout, {
      yPercent: 0,
      opacity: 1,
      duration: MOTION.dur.base,
      ease: MOTION.ease.entrance,
    });
}

function updateLinePrice(line, model) {
  const price = line.querySelector('.line__price');
  gsap
    .timeline()
    .to(price, { opacity: 0.35, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
    .set(price, { textContent: `$${model.qty * model.price}` })
    .to(price, { opacity: 1, duration: MOTION.dur.base, ease: MOTION.ease.entrance });
}

function removeLine(line, model) {
  cart.splice(cart.indexOf(model), 1);
  const wait = isReduced() ? 0 : MOTION.dur.base * 1000;

  if (!isReduced()) {
    gsap.to(line, {
      opacity: 0,
      x: 40,
      duration: MOTION.dur.base,
      ease: MOTION.ease.exit,
    });
    gsap.to(line, {
      height: 0,
      marginBottom: 0,
      paddingBottom: 0,
      duration: MOTION.dur.slow,
      ease: MOTION.ease.standard,
      delay: MOTION.dur.quick,
    });
  }

  setTimeout(() => {
    renderCart();
    bumpCount({ delta: 0 });
  }, wait + 100);
}

/* --- Initial render ------------------------------------------------------- */
function build() {
  renderCart();
  bumpCount({ delta: 0 });
  els.atbLabel.textContent = 'Add to bag';
  gsap.set(els.atbLabel, { yPercent: 0, opacity: 1 });
  hideFlash();
  closeDrawer();
}

build();
window.addEventListener('mo:replay', () => {
  cart.length = 0;
  nextId = 1;
  build();
});
